import React, { useCallback, useMemo, useState } from 'react';
import { arrayOf, bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import {
  Activity,
  IconSuccess,
  IconTrashBin,
  ModalContainer,
  SecondaryButton,
} from '..';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { findOptionsForSelectFilter } from '../../util/search';
import getCountryCodes from '../../translations/countryCodes';
import AddActivityForm from '../../forms/AddActivityForm/AddActivityForm';
import Flag from 'react-world-flags';

import css from './ProfileActivities.module.css';
import { useConfiguration } from '../../context/configurationContext';

const ProfileActivities = props => {
  const {
    intl,
    activities = {},
    documents = [],
    isEditMode,
    className,
    onUpdateProfile,
    onClearFormErrors,
    submitInProgress,
    submitError,
    userIntent,
    isCompany,
    currentUser,
    activitiesGridColumns,
  } = props;

  const config = useConfiguration();

  const [activityModalOpen, setActivityModalOpen] = useState(null);

  const isProvider = userIntent === 'provider';
  
  // Check if user has completed ALL legal compliance requirements
  const hasCompletedLegalCompliance = useMemo(() => {
    const legalCompliance = currentUser?.attributes?.profile?.protectedData?.legalCompliance;
    if (!legalCompliance) return false;
    
    // For companies: must have dataAccuracy and liabilityAcknowledgment
    // For individuals: must have all three (dataAccuracy, liabilityAcknowledgment, criminalRecordsClearance)
    const hasRequiredForCompany = legalCompliance.dataAccuracy && legalCompliance.liabilityAcknowledgment;
    const hasRequiredForIndividual = hasRequiredForCompany && legalCompliance.criminalRecordsClearance;
    
    return isCompany ? hasRequiredForCompany : hasRequiredForIndividual;
  }, [currentUser, isCompany]);

  const addActivityTitle = useMemo(
    () => (
      <FormattedMessage
        id={isProvider ? 'AddActivityForm.providerTitle' : 'AddActivityForm.clientTitle'}
      />
    ),
    [isProvider]
  );

  const activityOptions = useMemo(
    () =>
      findOptionsForSelectFilter('activity', config.search.defaultFilters).map(
        ({ key, label }) => ({
          key,
          label: intl.formatMessage({ id: label }),
        })
      ),
    [config.search.defaultFilters, intl]
  );

  const isActivityModalOpen = useMemo(() => activityModalOpen !== null, [activityModalOpen]);

  const activityKeys = useMemo(() => {
    if (!activities || typeof activities !== 'object') return [];
    return Object.keys(activities);
  }, [activities]);

  const hasAddedActivities = useMemo(() => activityKeys.length > 0, [activityKeys.length]);

  // Stabilize config.search.defaultFilters reference
  const defaultFilters = useMemo(() => config.search.defaultFilters, [
    JSON.stringify(config.search.defaultFilters)
  ]);

  const getActivityDisciplines = useCallback(
    activity => findOptionsForSelectFilter(activity, defaultFilters),
    [defaultFilters]
  );

  // Define modal control callback early to avoid circular dependencies
  const onCloseActivityModal = useCallback(() => {
    setActivityModalOpen(null);
    setEditingIndex(null);
    setEditingType(null);
    onClearFormErrors();
  }, [onClearFormErrors]);

  const handleSubmitAddActivity = useCallback(
    values => {
      const {
        activity: selectedActivities,
        discipline: selectedDisciplines,
        documents: selectedDocuments, // NEW: unified documents array
      } = values;

      const profileData = { publicData: { activities: { ...activities } } };
      const certificateOperations = [];
      const allSelectedDisciplines = [];

      // Build document metadata objects
      // Build document metadata for all document types
      // NOTE: We do NOT add these to profileData yet - the backend will add them after upload
      const newDocs = selectedDocuments.map(c => {
        console.log('[CREATE] Document from form:', c);
        return {
          fileName: c.file?.name,
          documentType: c.documentType,
          country: c.country,
          issuer: c.issuer,
          registerId: c.registerId,
          issuerUrl: c.issuerUrl,
          expiryDate: c.expiryDate && c.expiryDate.date ? c.expiryDate.date.toISOString() : (c.expiryDate || null),
          activities: selectedActivities.map(a => typeof a === 'object' ? a.key : a),
          disciplines: selectedDisciplines.map(d => typeof d === 'object' ? d.key : d),
          reviewStatus: 'pending',
          operatorNotes: null,
          uploadedAt: new Date().toISOString()
        };
      }).filter(doc => doc.fileName);

      // Store legal compliance checkboxes in protected data
      if (values.dataAccuracy || values.liabilityAcknowledgment || values.criminalRecordsClearance) {
        profileData.protectedData = profileData.protectedData || {};
        profileData.protectedData.legalCompliance = {
          dataAccuracy: values.dataAccuracy || false,
          liabilityAcknowledgment: values.liabilityAcknowledgment || false,
          criminalRecordsClearance: values.criminalRecordsClearance || false,
          confirmedAt: new Date().toISOString()
        };
      }

      selectedActivities.forEach(activityKey => {
        // Ensure activityKey is a string
        const key = typeof activityKey === 'object' ? activityKey.key : activityKey;
        
        if (!profileData.publicData.activities[key]) {
          profileData.publicData.activities[key] = [];
        }

        const activityDisciplines = getActivityDisciplines(key).map(d => d.key);
        const disciplinesForThisActivity = selectedDisciplines.map(d => typeof d === 'object' ? d.key : d).filter(d =>
          activityDisciplines.includes(d)
        );

        disciplinesForThisActivity.forEach(discipline => {
          const activityData = profileData.publicData.activities[key];
          const isDisciplineUsed = activityData.findIndex(
            activityInfo => activityInfo.discipline === discipline
          );

          const addValue = { discipline };

          if (isDisciplineUsed !== -1) {
            activityData[isDisciplineUsed] = addValue;
          } else {
            activityData.push(addValue);
          }

          allSelectedDisciplines.push(discipline);
        });
      });

      // Handle document uploads for all types
      // Build upload operations for backend with complete document metadata
      if (selectedDocuments && selectedDocuments.length > 0) {
        selectedDocuments.forEach((doc, idx) => {
          if (doc.file) {
            const docMetadata = newDocs[idx];
            console.log('[CREATE] Document from form:', doc);
            console.log('[CREATE] Metadata to send:', docMetadata);
            console.log('[CREATE] Activities in metadata:', docMetadata.activities);
            console.log('[CREATE] Disciplines in metadata:', docMetadata.disciplines);
            certificateOperations.push({
              certificates: doc.file,
              disciplines: docMetadata.disciplines,
              metadata: docMetadata, // Complete document metadata
              profileData: profileData // Activities and other profile data
            });
          }
        });
      }

      // AUTO-REVOCATION: Revoke verification for any disciplines being touched (added/updated)
      // This ensures that new/modified disciplines start as unverified until Operator approval.
      const currentVerification = currentUser?.attributes?.profile?.publicData?.verification || {};
      const newAccess = { ...(currentVerification.access || {}) };
      let hasRevocations = false;

      if (allSelectedDisciplines.length > 0) {
        allSelectedDisciplines.forEach(d => {
          newAccess[d] = false;
          hasRevocations = true;
        });
      }

      // WAF FIX: Sanitize documentStatusHistory keys
      // 1. Remove encoded characters like %2E
      // 2. Remove file extensions to align with Backend clean-up
      let cleanDocumentHistory = null;
      let hasHistoryChanges = false;
      
      if (currentVerification.documentStatusHistory) {
        cleanDocumentHistory = {};
        
        Object.entries(currentVerification.documentStatusHistory).forEach(([key, value]) => {
          let cleanKey = key;
          
          // Step 1: Decode URL encoding
          if (key.includes('%')) {
            try {
              cleanKey = decodeURIComponent(key);
            } catch (e) {
              cleanKey = key.replace(/%2E/g, '.');
            }
          }
          
          // Step 2: Remove extension (align with backend)
          // Only if it looks like a file with extension (e.g. "uuid.pdf")
          const lastDotIndex = cleanKey.lastIndexOf('.');
          if (lastDotIndex > 0) {
            cleanKey = cleanKey.substring(0, lastDotIndex);
          }
          
          if (cleanKey !== key) {
            hasHistoryChanges = true;
          }
          cleanDocumentHistory[cleanKey] = value;
        });
      }

      if (hasRevocations || hasHistoryChanges) {
        profileData.publicData.verification = {
          ...currentVerification,
          access: newAccess
        };
        
        // Use the sanitized history if available
        if (cleanDocumentHistory) {
          profileData.publicData.verification.documentStatusHistory = cleanDocumentHistory;
        }
      }

      onUpdateProfile(profileData, certificateOperations);
      
      // Close the modal after initiating the update
      onCloseActivityModal();
    },
    [activities, onUpdateProfile, documents, onCloseActivityModal, getActivityDisciplines, currentUser]
  );

  const removeDiscipline = useCallback(
    async data => {
      const { activity, disciplineIndex } = data;

      // Get discipline being removed
      const disciplineToRemove = activities[activity][disciplineIndex]?.discipline;
      
      // SAFETY CHECK: If user has documents, check if this discipline is used in any document
      if (documents && documents.length > 0) {
        const isDisciplineInDocuments = documents.some(doc => 
          doc.disciplines && doc.disciplines.includes(disciplineToRemove)
        );
        
        if (isDisciplineInDocuments) {
          console.warn('[Remove Discipline] Cannot remove discipline - it is associated with uploaded documents:', disciplineToRemove);
          alert(`Cannot remove "${disciplineToRemove}" - it is associated with your uploaded documents. Please delete the relevant documents first.`);
          return;
        }
      }

      const profileData = { publicData: { activities: {} } };

      const activityData = { ...activities };
      activityData[activity].splice(disciplineIndex, 1);

      profileData.publicData.activities = { ...activityData };

      if (activityData[activity].length === 0) {
        const filteredActivities = Object.keys(activityData).reduce((acc, activityName) => {
          if (activityName !== activity) acc[activityName] = activityData[activityName];
          return acc;
        }, {});

        profileData.publicData.activities = { ...filteredActivities };
      }

      // CRITICAL: For customers, ONLY update publicData.activities
      // DO NOT pass third parameter which would trigger document deletion API
      onUpdateProfile(profileData);
    },
    [activities, documents, onUpdateProfile]
  );

  const [editingIndex, setEditingIndex] = useState(null);
  const [editingType, setEditingType] = useState(null); // 'certificate' or 'insurance'

  const openActivityModal = useCallback(
    (data = null, index = null, type = null) => {
      setEditingIndex(index);
      setEditingType(type);

      let initialValues = {
        activity: [],
        discipline: [],
        documents: [],
      };

      if (data) {
        // Ensure activities and disciplines are arrays
        const ensureArray = (val) => {
          if (!val) return [];
          if (Array.isArray(val)) return val;
          return [val];
        };
        
        // If editing existing document
        if (type === 'certificate' || type === 'document') {
           initialValues = {
             activity: ensureArray(data.activities),
             discipline: ensureArray(data.disciplines),
             documents: [{
               file: { name: data.fileName }, // Mock file for display
               documentType: data.documentType,
               country: data.country,
               issuer: data.issuer,
               registerId: data.registerId,
               issuerUrl: data.issuerUrl,
               expiryDate: data.expiryDate ? { date: new Date(data.expiryDate) } : null,
             }]
           };
        } else if (type === 'insurance') {
           initialValues = {
             activity: ensureArray(data.activities),
             discipline: [],
             documents: [{
               file: { name: data.fileName },
               documentType: 'insurance_coverage',
               expiryDate: data.expiryDate ? { date: new Date(data.expiryDate) } : null,
             }]
           };
        } else {
           // Other data
           initialValues = { ...data };
        }
      }
      
      // Always add legal compliance from protectedData if available and user is adding new activities (not editing)
      const legalCompliance = currentUser?.attributes?.profile?.protectedData?.legalCompliance;
      if (legalCompliance && !data) {
        initialValues.dataAccuracy = legalCompliance.dataAccuracy || false;
        initialValues.liabilityAcknowledgment = legalCompliance.liabilityAcknowledgment || false;
        initialValues.criminalRecordsClearance = legalCompliance.criminalRecordsClearance || false;
      }

      setActivityModalOpen(initialValues);
    },
    [currentUser]
  );

  const handleDelete = useCallback((index, type) => {
    const profileData = {};
    
    // REVOCATION LOGIC: Get disciplines from the doc being deleted
    const docToDelete = documents[index];
    const affectedDisciplines = docToDelete?.disciplines || [];

    const currentVerification = currentUser?.attributes?.profile?.publicData?.verification || {};
    const newAccess = { ...(currentVerification.access || {}) };
    
    let hasRevocations = false;
    // Revoke verification for any discipline associated with the deleted document
    if (affectedDisciplines && Array.isArray(affectedDisciplines)) {
      affectedDisciplines.forEach(d => {
          newAccess[d] = false;
          hasRevocations = true;
      });
    }

    // Find and remove document from unified documents array
    const newDocs = [...documents];
    newDocs.splice(index, 1);
    
    // Update protected data - this will sync to Sharetribe
    profileData.protectedData = { documents: newDocs };
    
    // Only update verification if we have revocations
    if (hasRevocations) {
      profileData.publicData = {
        verification: {
          ...currentVerification,
          access: newAccess
        }
      };
    }
    
    // Update profile directly without calling backend delete API
    // The backend will sync publicData.documents on next upload/login
    onUpdateProfile(profileData);
  }, [documents, onUpdateProfile, currentUser]);

  const getCountryName = useCallback((code) => {
    const codes = getCountryCodes(intl.locale);
    const found = codes.find(c => c.code === code);
    return found ? found.name : code;
  }, [intl.locale]);

  // Wrap handleSubmit to handle editing
  const handleFormSubmit = useCallback((values) => {
    if (editingIndex !== null) {
      // Handle Update
      const {
        activity: selectedActivities,
        discipline: selectedDisciplines,
        documents: selectedDocuments,
      } = values;

      // Separate by documentType
      const selectedCertificates = selectedDocuments?.filter(d => d.documentType === 'professional_certificate') || [];
      const selectedInsurance = selectedDocuments?.filter(d => d.documentType === 'liability_insurance') || [];

      const profileData = {};
      const certificateOperations = [];

      if (editingType === 'document' && selectedDocuments?.length > 0) {
         // Generic handler for ALL document types
         const doc = selectedDocuments[0]; // Assume single edit
         const existingEntry = documents[editingIndex];
         
         console.log('[EDIT] Document from form:', doc);
         console.log('[EDIT] Existing entry:', existingEntry);
         
         // Check if file has changed or critical fields modified
         const fileChanged = doc.file && doc.file.name && doc.file.name !== existingEntry.fileName;
         const criticalFieldsChanged = 
           doc.country !== existingEntry.country ||
           doc.issuer !== existingEntry.issuer ||
           doc.registerId !== existingEntry.registerId ||
           (doc.expiryDate && doc.expiryDate.date && doc.expiryDate.date.toISOString() !== existingEntry.expiryDate);
         
        // Always set to pending on any edit to ensure operator reviews changes
        const newReviewStatus = 'pending';
         
         const updatedDoc = {
           ...existingEntry,
           fileName: doc.file?.name || existingEntry.fileName,
           documentType: doc.documentType,
           country: doc.country,
           issuer: doc.issuer,
           registerId: doc.registerId,
           issuerUrl: doc.issuerUrl,
           expiryDate: doc.expiryDate && doc.expiryDate.date ? doc.expiryDate.date.toISOString() : (doc.expiryDate || existingEntry.expiryDate),
           activities: selectedActivities.map(a => typeof a === 'object' ? a.key : a),
           disciplines: selectedDisciplines.map(d => typeof d === 'object' ? d.key : d),
           // CRITICAL: Preserve file storage fields only if file not changed
           url: fileChanged ? null : existingEntry?.url,
           key: fileChanged ? null : existingEntry?.key,
           bucket: fileChanged ? null : existingEntry?.bucket,
           reviewStatus: newReviewStatus,
           operatorNotes: null
         };
         
         // If file changed, add to certificateOperations for S3 upload
         if (fileChanged && doc.file) {
           console.log('[EDIT] File changed - metadata sent to backend:', updatedDoc);
           certificateOperations.push({
             certificates: doc.file,
             disciplines: updatedDoc.disciplines,
             metadata: {
               ...updatedDoc,
               editingIndex: editingIndex // Tell backend which document to replace
             },
             profileData: profileData
           });
           
           // Do NOT update protectedData.documents when file changes
           // Backend will handle the complete update after S3 upload
         } else {
           // Only update protectedData when metadata-only change (no file upload)
           const newDocs = [...documents];
           newDocs[editingIndex] = updatedDoc;
           profileData.protectedData = { documents: newDocs };
         }
         
         // Backend handles publicData.documents construction
         profileData.publicData = {}; 

         // REVOCATION LOGIC for Edit: edits always set pending above,
         // so revoke verification for affected disciplines to ensure re-review.
         {
           const currentVerification = currentUser?.attributes?.profile?.publicData?.verification || {};
           const newAccess = { ...(currentVerification.access || {}) };
           const affectedDisciplines = updatedDoc.disciplines || [];
           let hasRevocations = false;

           affectedDisciplines.forEach(d => {
             newAccess[d] = false;
             hasRevocations = true;
           });

           if (hasRevocations) {
             profileData.publicData = {
               verification: {
                 ...currentVerification,
                 access: newAccess
               }
             };
           } else {
             profileData.publicData = {};
           }
         }

      } else if (editingType === 'certificate' && selectedCertificates.length > 0) {
         const cert = selectedCertificates[0]; // Assume single edit
         const existingEntry = documents[editingIndex];
         const updatedDoc = {
           ...existingEntry,
           fileName: cert.file?.name || existingEntry.fileName,
           documentType: 'professional_certificate',
           country: cert.country,
           issuer: cert.issuer,
           registerId: cert.registerId,
           issuerUrl: cert.issuerUrl,
           expiryDate: cert.expiryDate && cert.expiryDate.date ? cert.expiryDate.date.toISOString() : (cert.expiryDate || existingEntry.expiryDate),
           activities: selectedActivities.map(a => typeof a === 'object' ? a.key : a),
           disciplines: selectedDisciplines.map(d => typeof d === 'object' ? d.key : d),
          // CRITICAL: Preserve file storage fields; always set review to pending
          url: existingEntry?.url,
          key: existingEntry?.key,
          bucket: existingEntry?.bucket,
          reviewStatus: 'pending',
           operatorNotes: null
         };
         
         const newDocs = [...documents];
         newDocs[editingIndex] = updatedDoc;
         
         profileData.protectedData = { documents: newDocs };
         // Backend handles publicData.documents construction
         profileData.publicData = {};

      } else if (editingType === 'insurance' && selectedInsurance.length > 0) {
         const ins = selectedInsurance[0];
         const existingEntry = documents[editingIndex];
         const updatedDoc = {
           ...existingEntry,
           fileName: ins.file?.name || existingEntry.fileName,
           documentType: 'liability_insurance',
           expiryDate: ins.expiryDate && ins.expiryDate.date ? ins.expiryDate.date.toISOString() : (ins.expiryDate || existingEntry.expiryDate),
           // CRITICAL: Preserve file storage fields
          url: existingEntry?.url,
          key: existingEntry?.key,
          bucket: existingEntry?.bucket,
          reviewStatus: 'pending',
           operatorNotes: null
         };
         
         const newDocs = [...documents];
         newDocs[editingIndex] = updatedDoc;
         
         profileData.protectedData = { documents: newDocs };
         // Backend handles publicData.documents construction
         profileData.publicData = {};
      }

      onUpdateProfile(profileData, certificateOperations);
      onCloseActivityModal();

    } else {
      // Handle Create (Original Logic)
      handleSubmitAddActivity(values);
    }
  }, [editingIndex, editingType, documents, onUpdateProfile, handleSubmitAddActivity, onCloseActivityModal]);

  // Helper function to get document type icon and label
  const getDocumentTypeInfo = useCallback((docType) => {
    const types = {
      professional_certificate: { 
        icon: '📜', 
        label: intl.formatMessage({ id: 'AddActivityForm.docTypeProfessionalCert', defaultMessage: 'Professional Certificate' })
      },
      association_registration: { 
        icon: '🎓', 
        label: intl.formatMessage({ id: 'AddActivityForm.docTypeAssociationReg', defaultMessage: 'Association Registration' })
      },
      safety_certificate: { 
        icon: '🚑', 
        label: intl.formatMessage({ id: 'AddActivityForm.docTypeSafetyCert', defaultMessage: 'Safety Certificate' })
      },
      insurance_coverage: { 
        icon: '🛡️', 
        label: intl.formatMessage({ id: 'AddActivityForm.docTypeInsuranceCoverage', defaultMessage: 'Proof of Insurance Coverage' })
      },
      // Company types
      chamber_of_commerce: { 
        icon: '🏢', 
        label: intl.formatMessage({ id: 'AddActivityForm.docTypeChamberCommerce', defaultMessage: 'Chamber of Commerce' })
      },
      business_license: { 
        icon: '📋', 
        label: intl.formatMessage({ id: 'AddActivityForm.docTypeBusinessLicense', defaultMessage: 'Business License' })
      },
    };
    return types[docType] || { 
      icon: '📄', 
      label: intl.formatMessage({ id: 'General.document', defaultMessage: 'Document' })
    };
  }, [intl]);

  // Get verification access for badge display
  const verificationAccess = useMemo(() => {
    return currentUser?.attributes?.profile?.publicData?.verification?.access || {};
  }, [currentUser]);

  return (
    <div className={classNames(css.root, className)}>
      {/* DISCIPLINES SECTION - Always shown for both providers and customers */}
      <h3 className={css.sectionTitle}>
        <FormattedMessage id="General.disciplines" />
      </h3>

      <div className={css.customerView}>
        {activityKeys?.length > 0 ? (
          <div
            className={css.activitiesGrid}
            style={activitiesGridColumns ? { gridTemplateColumns: `repeat(${activitiesGridColumns}, 1fr)` } : undefined}
          >
            {activityKeys.map(activity => {
              const disciplines = activities[activity];
              
              // We do NOT want to show badges on the Activity Header level, 
              // even if the verification key matches the activity name.
              // Status should only be shown on individual discipline chips.
              const isVerified = false; 
              const isPending = false;
              
              return (
                <div key={activity} className={css.activityCard}>
                  <div className={css.activityHeader}>
                    <Activity activity={activity} hasIconBackground />
                    {isVerified && (
                      <span className={css.verifiedBadge} title="Verified">
                        <IconSuccess /> <FormattedMessage id="ProfileActivities.verified" defaultMessage="Verified" />
                      </span>
                    )}
                    {isPending && (
                      <span className={css.pendingBadge} title="Pending Verification">
                        <FormattedMessage id="ProfileActivities.pendingVerification" defaultMessage="Pending" />
                      </span>
                    )}
                  </div>
                  <div className={css.disciplineChipList}>
                    {disciplines.map((activityProp, disciplineIndex) => {
                      const { discipline } = activityProp;
                      
                      // Check if this discipline has associated documents
                      const hasDocuments = documents && documents.some(doc => 
                        doc.disciplines && doc.disciplines.includes(discipline)
                      );
                      
                      // Check verification status for this specific discipline
                      const isDisciplineVerified = isProvider && verificationAccess[discipline] === true;
                      const isDisciplinePending = isProvider && verificationAccess[discipline] === false;
                      
                      // Only customers can remove disciplines (unless they have documents)
                      const canRemove = isEditMode && !isProvider && !hasDocuments;
                      
                      // Determine chip style based on verification
                      const chipClass = classNames(css.disciplineChip, {
                        [css.disciplineChipVerified]: isDisciplineVerified,
                        [css.disciplineChipPending]: isDisciplinePending,
                      });
                      
                      return (
                        <div key={discipline} className={chipClass}>
                          {isDisciplineVerified && (
                            <IconSuccess className={css.disciplineIcon} />
                          )}
                          <span className={css.disciplineChipText}>
                            <FormattedMessage id={`General.${discipline}`} />
                          </span>
                          {canRemove && (
                            <button
                              type="button"
                              className={css.chipRemoveBtn}
                              onClick={() => removeDiscipline({ activity, disciplineIndex })}
                              aria-label="Remove discipline"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={css.emptyState}>
            <div className={css.emptyStateIcon}>📋</div>
            <p className={css.emptyStateText}>
              <FormattedMessage 
                id={isProvider ? "ProfileActivities.noDisciplinesYetProvider" : "ProfileActivities.noDisciplinesYetCustomer"} 
              />
            </p>
          </div>
        )}
      </div>

      {/* CERTIFICATES SECTION - Only for providers in edit mode */}
      {isProvider && isEditMode && (
        <>
          <h3 className={css.sectionTitle} style={{ marginTop: '2rem' }}>
            <FormattedMessage id="General.certificates" />
          </h3>
          
          <div className={css.providerView}>
          {Array.isArray(documents) && documents.length > 0 ? (
            <div className={css.documentsGrid}>
              {documents
                .map((doc, index) => ({ doc, originalIndex: index }))
                .filter(({ doc }) => doc)
                .map(({ doc, originalIndex }) => {
                  const disciplinesList = Array.isArray(doc.disciplines) 
                    ? doc.disciplines 
                    : (doc.disciplines ? [doc.disciplines] : []);
                  
                  const activitiesList = Array.isArray(doc.activities)
                    ? doc.activities
                    : (doc.activities ? [doc.activities] : []);

                  const isExpired = doc.expiryDate && new Date(doc.expiryDate) < new Date();
                  const isExpiringSoon = doc.expiryDate && 
                    new Date(doc.expiryDate) > new Date() &&
                    new Date(doc.expiryDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

                  const docTypeInfo = getDocumentTypeInfo(doc.documentType);
                  
                  // Check verification status for this document's disciplines
                  const allDisciplinesVerified = disciplinesList.length > 0 && 
                    disciplinesList.every(d => verificationAccess[d] === true);
                  const anyDisciplineNotVerified = disciplinesList.some(d => verificationAccess[d] === false);
                  
                  // Get badge class based on status
                  let badgeClass = css.badge_default;
                  if (doc.reviewStatus === 'pending') badgeClass = css.badge_pending;
                  if (doc.reviewStatus === 'approved') badgeClass = css.badge_approved;
                  if (doc.reviewStatus === 'rejected') badgeClass = css.badge_rejected;
                  if (doc.reviewStatus === 'expired') badgeClass = css.badge_expired;
                  
                  // Get header background class based on status
                  let headerClass = css.documentCardHeader;
                  if (doc.reviewStatus === 'pending') headerClass = classNames(css.documentCardHeader, css.header_pending);
                  if (doc.reviewStatus === 'approved') headerClass = classNames(css.documentCardHeader, css.header_approved);
                  if (doc.reviewStatus === 'rejected') headerClass = classNames(css.documentCardHeader, css.header_rejected);
                  if (doc.reviewStatus === 'expired') headerClass = classNames(css.documentCardHeader, css.header_expired);

                  return (
                    <div key={originalIndex} className={css.documentCard}>
                      <div className={headerClass}>
                        <div className={css.documentTypeIconLarge}>{docTypeInfo.icon}</div>
                        <div className={css.documentInfo}>
                          <div 
                            className={badgeClass}
                            title={`Status: ${doc.reviewStatus || 'none'}`}
                          >
                            {docTypeInfo.label}
                          </div>
                          <h4 className={css.documentTitle}>{doc.fileName || 'Document'}</h4>
                          <div className={css.documentMeta}>
                            {doc.uploadedAt && (
                              <span className={css.uploadDate}>
                                {new Date(doc.uploadedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        {doc.reviewStatus && (
                          <span className={classNames(
                            css.statusBadge,
                            doc.reviewStatus === 'pending' ? css['status-pending'] :
                            doc.reviewStatus === 'approved' ? css['status-approved'] :
                            doc.reviewStatus === 'rejected' ? css['status-rejected'] :
                            doc.reviewStatus === 'expired' ? css['status-expired'] : ''
                          )}>
                            {doc.reviewStatus === 'approved' && (
                              <>
                                <IconSuccess />{' '}
                              </>
                            )}
                            <FormattedMessage id={`ProfileActivities.status.${doc.reviewStatus}`} defaultMessage={doc.reviewStatus} />
                          </span>
                        )}
                      </div>

                      <div className={css.documentBody}>
                        <div className={css.documentMetadata}>
                          {doc.country && (
                            <div className={css.metaRow}>
                              <span className={css.metaLabel}>
                                <FormattedMessage id="ProfileActivities.country" defaultMessage="Country:" />
                              </span>
                              <span className={css.metaValue}>{getCountryName(doc.country)}</span>
                            </div>
                          )}
                          {doc.issuer && (
                            <div className={css.metaRow}>
                              <span className={css.metaLabel}>
                                <FormattedMessage id="ProfileActivities.issuer" defaultMessage="Issuer:" />
                              </span>
                              <span className={css.metaValue}>{doc.issuer}</span>
                            </div>
                          )}
                          {doc.registerId && (
                            <div className={css.metaRow}>
                              <span className={css.metaLabel}>
                                <FormattedMessage id="ProfileActivities.registerId" defaultMessage="Register ID:" />
                              </span>
                              <span className={css.metaValue}>{doc.registerId}</span>
                            </div>
                          )}
                          {doc.expiryDate && (
                            <div className={css.metaRow}>
                              <span className={css.metaLabel}>
                                <FormattedMessage id="ProfileActivities.expires" defaultMessage="Expires:" />
                              </span>
                              <span className={classNames(css.metaValue, {
                                [css.expired]: isExpired,
                                [css.expiring]: isExpiringSoon
                              })}>
                                {new Date(doc.expiryDate).toLocaleDateString()}
                                {isExpired && <FormattedMessage id="ProfileActivities.expired" defaultMessage=" (Expired)" />}
                                {isExpiringSoon && <FormattedMessage id="ProfileActivities.expiringSoon" defaultMessage=" (Expiring Soon)" />}
                              </span>
                            </div>
                          )}
                        </div>

                        {disciplinesList.length > 0 && (
                          <div className={css.linkedActivities}>
                            <div className={css.linkedLabel}>
                              <FormattedMessage id="ProfileActivities.validFor" defaultMessage="Valid for:" />
                            </div>
                            <div className={css.disciplineChipList}>
                              {disciplinesList.map((discipline, i) => (
                                <div key={`discipline-${i}`} className={css.disciplineChip}>
                                  <span className={css.disciplineChipText}>
                                    <FormattedMessage id={`General.${discipline}`} />
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Display operator notes if present (for rejected/expired statuses) */}
                        {doc.operatorNotes && ['rejected', 'expired'].includes(doc.reviewStatus) && (
                          <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: doc.reviewStatus === 'rejected' 
                              ? 'rgba(237, 60, 60, 0.1)' 
                              : 'rgba(255, 107, 53, 0.1)',
                            border: doc.reviewStatus === 'rejected'
                              ? '1px solid var(--failColor)'
                              : '1px solid #ff6b35'
                          }}>
                            <div style={{
                              fontWeight: 600,
                              marginBottom: '8px',
                              color: '#4a4a4a'
                            }}>
                              <FormattedMessage 
                                id={doc.reviewStatus === 'rejected' ? 'ProfileActivities.rejectionReason' : 'ProfileActivities.notice'}
                                defaultMessage={doc.reviewStatus === 'rejected' ? 'Rejection Reason' : 'Notice'}
                              />
                            </div>
                            <p style={{
                              color: '#4a4a4a',
                              lineHeight: '1.5',
                              margin: 0
                            }}>{doc.operatorNotes}</p>
                          </div>
                        )}
                      </div>

                      {isEditMode && (
                        <div className={css.documentActions}>
                          <button
                            type="button"
                            className={css.actionBtn}
                            onClick={() => openActivityModal(doc, originalIndex, 'document')}
                          >
                            <FormattedMessage id="ProfileActivities.edit" defaultMessage="Edit" />
                          </button>
                          <button
                            type="button"
                            className={classNames(css.actionBtn, css.dangerBtn)}
                            onClick={() => handleDelete(originalIndex, 'document')}
                          >
                            <FormattedMessage id="ProfileActivities.delete" defaultMessage="Delete" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className={css.emptyState}>
              <div className={css.emptyStateIcon}>📄</div>
              <p className={css.emptyStateText}>
                <FormattedMessage id="ProfileActivities.noDocumentsYet" />
              </p>
            </div>
          )}
          </div>
        </>
      )}

      {isEditMode && submitError}
      {isEditMode && (
        <SecondaryButton
          type="button"
          className={classNames(css.addButton, css.formMargins)}
          onClick={() => openActivityModal()}
        >
          <FormattedMessage id="ProfileSettingsForm.add" />
          <span className={css.addButtonPlus}>+</span>
        </SecondaryButton>
      )}

      {isEditMode && (
        <ModalContainer
          id="AddActivityModal"
          title={addActivityTitle}
          isModalOpen={isActivityModalOpen}
          onClose={onCloseActivityModal}
        >
          {isActivityModalOpen && (
            <AddActivityForm
              updateInProgress={submitInProgress}
              updateError={submitError}
              initialValues={activityModalOpen}
              activityOptions={activityOptions}
              getActivityDisciplines={getActivityDisciplines}
              onClearFormErrors={onClearFormErrors}
              onSubmit={handleFormSubmit}
              userIntent={userIntent}
              isCompany={isCompany}
              hasCompletedLegalCompliance={hasCompletedLegalCompliance}
            />
          )}
        </ModalContainer>
      )}
    </div>
  );
};

ProfileActivities.propTypes = {
  intl: intlShape.isRequired,
  className: string,
  activities: object.isRequired,
  documents: arrayOf(object),
  isEditMode: bool,
  onUpdateProfile: func,
  onClearFormErrors: func,
  submitInProgress: bool,
  submitError: object,
  isCompany: bool,
  userIntent: string,
  currentUser: object,
};

export default ProfileActivities;
